import type { GetChildListParams } from '@repo/api/parent/parentService';
import type { ChildrenResourceResult } from '@repo/api/parent/useChildrenResource';
import type { ParentAttendanceSummary, ParentChildAttendance } from '@repo/types/parent';
import type { UserPublic } from '@repo/types/users';

import { useGetChildrenAssignments } from '@repo/api/parent/useGetChildrenAssignments';
import { useGetChildrenAttendance } from '@repo/api/parent/useGetChildrenAttendance';
import { useGetChildrenAttendanceSummary } from '@repo/api/parent/useGetChildrenAttendanceSummary';
import { useGetChildrenGrades } from '@repo/api/parent/useGetChildrenGrades';
import { useGetChildrenGroups } from '@repo/api/parent/useGetChildrenGroups';
import { useGetChildrenQuizzes } from '@repo/api/parent/useGetChildrenQuizzes';
import { useGetMyChildren } from '@repo/api/parent/useGetMyChildren';
import { cn } from '@repo/lib/cn';
import { format, parseISO } from 'date-fns';
import { Dot, TriangleAlertIcon } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router';

import GradeCard from '@/components/features/grades/GradeCard';
import { STATUS_CONFIG, STATUS_ORDER } from '@/components/features/groups/attendance/AttendanceTable';
import { ChildSectionHeader } from '@/components/features/parent/ChildSectionList';
import ParentPageHeader from '@/components/features/parent/layout/ParentPageHeader';
import { ParentAssignmentCard } from '@/components/features/parent/ParentAssignmentCard';
import { ParentAssignmentDialog } from '@/components/features/parent/ParentAssignmentDialog';
import { ParentGroupPreviewSection } from '@/components/features/parent/ParentGroupPreviewSection';
import { ParentQuizAttemptCard } from '@/components/features/parent/ParentQuizAttemptCard';
import { ParentQuizAttemptDialog } from '@/components/features/parent/ParentQuizAttemptDialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';
import { EmptyState, EmptyStateContent, EmptyStateDescription } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { useChildSectionCollapse } from '@/hooks/parent';

function parseGroupId(raw: string | undefined): number | undefined {
  const parsed = Number(raw);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
}

function dataFor<TData>(result: ChildrenResourceResult<TData>, childId: number): TData | undefined {
  return result.results.find(({ child }) => child.id === childId)?.data;
}

function hasFailed<TData>(result: ChildrenResourceResult<TData>, childId: number): boolean {
  return result.failed.some((child) => child.id === childId);
}

export default function ParentGroupPage() {
  const { id } = useParams();
  const groupId = parseGroupId(id);

  const { data: children = [], isPending: isPendingChildren, isError, errorMessage } = useGetMyChildren();

  const groups = useGetChildrenGroups(children, !isPendingChildren);

  const members = useMemo(() => groups.results.filter(({ data }) => data.some((group) => group.id === groupId)).map(({ child }) => child), [groups.results, groupId]);

  const group = useMemo(() => {
    for (const { data } of groups.results) {
      const match = data.find((childGroup) => childGroup.id === groupId);
      if (match) return match;
    }
    return undefined;
  }, [groups.results, groupId]);

  const collapse = useChildSectionCollapse(members[0]?.id);

  const previewParamsFor = useCallback((): GetChildListParams => {
    const params: GetChildListParams = { page: 0, pageSize: 5 };
    if (groupId !== undefined) {
      params.groupId = groupId;
    }
    return params;
  }, [groupId]);

  const grades = useGetChildrenGrades(members, previewParamsFor);
  const attendance = useGetChildrenAttendance(members, previewParamsFor);
  const assignments = useGetChildrenAssignments(members, previewParamsFor);
  const quizzes = useGetChildrenQuizzes(members, previewParamsFor);
  const summaries = useGetChildrenAttendanceSummary(members);

  return (
    <div className={'base-direction-col'}>
      <div className={'flex w-full grow flex-col gap-6'}>
        <ParentPageHeader title={group?.name ?? 'Grupa'} />

        {isPendingChildren || groups.isPending ? (
          <div className={'flex flex-col gap-6'}>
            <Skeleton className={'h-32 w-full'} />
            <Skeleton className={'h-32 w-full'} />
          </div>
        ) : isError || groups.isError ? (
          <Alert variant={'destructive'}>
            <TriangleAlertIcon />
            <AlertDescription>{errorMessage?.detail ?? 'Nie udało się pobrać danych grupy.'}</AlertDescription>
          </Alert>
        ) : groupId === undefined || (!members.length && !groups.failed.length) ? (
          <EmptyState>
            <EmptyStateContent>
              <EmptyStateDescription>Nie znaleziono grupy lub żadne z dzieci do niej nie należy</EmptyStateDescription>
            </EmptyStateContent>
          </EmptyState>
        ) : (
          <div className={'flex flex-col gap-2'}>
            {members.map((child) => (
              <MemberSection
                key={child.id}
                child={child}
                groupId={groupId}
                grades={grades}
                attendance={attendance}
                assignments={assignments}
                quizzes={quizzes}
                summaries={summaries}
                expanded={collapse.isExpanded(child.id)}
                onExpandedChange={(open) => collapse.setExpanded(child.id, open)}
              />
            ))}

            {groups.failed.map((child) => (
              <section key={child.id} className={'flex flex-col gap-3'}>
                <ChildSectionHeader child={child} />
                <ChildResourceWarning />
              </section>
            ))}
          </div>
        )}
      </div>

      <ParentAssignmentDialog />
      <ParentQuizAttemptDialog />
    </div>
  );
}

interface MemberSectionProps {
  child: UserPublic;
  groupId: number;
  grades: ReturnType<typeof useGetChildrenGrades>;
  attendance: ReturnType<typeof useGetChildrenAttendance>;
  assignments: ReturnType<typeof useGetChildrenAssignments>;
  quizzes: ReturnType<typeof useGetChildrenQuizzes>;
  summaries: ReturnType<typeof useGetChildrenAttendanceSummary>;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
}

function MemberSection(memberSectionProps: MemberSectionProps) {
  const { child, groupId, grades, attendance, assignments, quizzes, summaries, expanded, onExpandedChange } = memberSectionProps;

  const rate = dataFor(summaries, child.id)?.rate;

  return (
    <Collapsible open={expanded} onOpenChange={onExpandedChange} asChild>
      <section className={'flex flex-col gap-4'}>
        <ChildSectionHeader child={child} summary={rate === undefined || rate === null ? undefined : `Frekwencja: ${rate}%`} collapsible />

        <CollapsibleContent className={'flex flex-col gap-4'}>
          {summaries.isPending ? <Skeleton className={'h-24 w-full'} /> : hasFailed(summaries, child.id) ? <ChildResourceWarning /> : <GlobalAttendanceCard summary={dataFor(summaries, child.id)} />}

          <ParentGroupPreviewSection
            title={'Oceny'}
            moreHref={`/parent/grades?groupId=${groupId}`}
            data={dataFor(grades, child.id)}
            isPending={grades.isPending}
            isError={hasFailed(grades, child.id)}
            emptyMessage={'Brak ocen'}
          >
            {(rows) => (
              <div className={'border-border-primary bg-background [&>*:nth-child(odd)]:bg-card/25 [&>*:not(:last-child)]:border-border-primary/75 rounded-2xl border [&>*:not(:last-child)]:border-b'}>
                {rows.map((grade) => (
                  <GradeCard key={grade.id} grade={grade} className={'px-4'} />
                ))}
              </div>
            )}
          </ParentGroupPreviewSection>

          <ParentGroupPreviewSection
            title={'Frekwencja'}
            moreHref={`/parent/attendance?groupId=${groupId}`}
            data={dataFor(attendance, child.id)}
            isPending={attendance.isPending}
            isError={hasFailed(attendance, child.id)}
            emptyMessage={'Brak zarejestrowanej obecności'}
          >
            {(rows) => (
              <ul className={'border-border-primary bg-background flex flex-col rounded-2xl border'}>
                {rows.map((record) => (
                  <AttendanceRecordRow key={`${record.meetingId}-${record.userId}`} record={record} />
                ))}
              </ul>
            )}
          </ParentGroupPreviewSection>

          <ParentGroupPreviewSection
            title={'Zadania'}
            moreHref={`/parent/assignments?groupId=${groupId}`}
            data={dataFor(assignments, child.id)}
            isPending={assignments.isPending}
            isError={hasFailed(assignments, child.id)}
            emptyMessage={'Brak zadań'}
          >
            {(rows) => (
              <ul className={'flex flex-col gap-2'}>
                {rows.map((assignment) => (
                  <ParentAssignmentCard key={assignment.id} assignment={assignment} childId={child.id} />
                ))}
              </ul>
            )}
          </ParentGroupPreviewSection>

          <ParentGroupPreviewSection
            title={'Quizy'}
            moreHref={`/parent/quizzes?groupId=${groupId}`}
            data={dataFor(quizzes, child.id)}
            isPending={quizzes.isPending}
            isError={hasFailed(quizzes, child.id)}
            emptyMessage={'Brak rozwiązanych quizów'}
          >
            {(rows) => (
              <ul className={'flex flex-col gap-2'}>
                {rows.map((attempt) => (
                  <ParentQuizAttemptCard key={attempt.id} attempt={attempt} childId={child.id} />
                ))}
              </ul>
            )}
          </ParentGroupPreviewSection>
        </CollapsibleContent>
      </section>
    </Collapsible>
  );
}

function ChildResourceWarning() {
  return (
    <Alert variant={'warning'}>
      <TriangleAlertIcon />
      <AlertDescription>Nie udało się pobrać danych tego dziecka. Sprawdź, czy konto jest nadal powiązane.</AlertDescription>
    </Alert>
  );
}

// The child's all-time attendance, deliberately NOT filtered to this group —
// the caption spells that out so it is not read as a group figure.
function GlobalAttendanceCard({ summary }: { summary: ParentAttendanceSummary | undefined }) {
  if (!summary) return null;

  return (
    <div className={'border-border-primary bg-background flex flex-col gap-2 rounded-2xl border p-4'}>
      <div className={'flex flex-wrap items-baseline gap-x-2 gap-y-1'}>
        <h3 className={'text-foreground text-sm font-semibold'}>Frekwencja ogólna</h3>
        <span className={'text-muted-foreground text-xs'}>wszystkie zajęcia — spóźnienia nie liczą się jako obecność</span>
      </div>

      <div className={'flex flex-wrap items-center gap-3'}>
        <span className={'text-foreground text-2xl font-semibold'}>{summary.rate === null ? '—' : `${summary.rate}%`}</span>
        <div className={'flex flex-wrap gap-2'}>
          {STATUS_ORDER.map((status) => (
            <Badge key={status} variant={'muted'} size={'sm'} border>
              <Dot className={cn('-mx-1', STATUS_CONFIG[status].dotClassName)} />
              {STATUS_CONFIG[status].summaryLabel}: {summary[status]}
            </Badge>
          ))}
          <Badge variant={'muted'} size={'sm'} border>
            Łącznie: {summary.total}
          </Badge>
        </div>
      </div>
    </div>
  );
}

function AttendanceRecordRow({ record }: { record: ParentChildAttendance }) {
  const { meeting, status } = record;
  const statusConfig = status ? STATUS_CONFIG[status] : null;

  return (
    <li className={'border-border-primary/75 flex items-center gap-3 border-b px-4 py-2 last:border-b-0'}>
      <div className={'flex min-w-0 flex-col'}>
        <span className={'text-foreground truncate text-sm font-medium'}>{meeting.name}</span>
        <span className={'text-muted-foreground text-xs'}>{format(parseISO(meeting.startDate), 'd MMM yyyy, H:mm')}</span>
      </div>

      {statusConfig ? (
        <Badge size={'sm'} variant={'muted'} border className={cn('ms-auto', statusConfig.cellClassName)}>
          {statusConfig.label}
        </Badge>
      ) : (
        <span className={'text-muted-foreground ms-auto text-sm'}>—</span>
      )}
    </li>
  );
}
